import { useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Icon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@eluve/components';

import { EmptyState } from './EmptyState';
import { PanelHeader } from './PanelHeader';

export type SelectedEntity =
  | { type: 'org' }
  | { type: 'user'; userId: string; userName: string };

interface UserWithOverrideCount {
  userId: string;
  name: string;
  email: string;
  overrideCount: number;
}

interface EntitySelectorPanelProps {
  users: UserWithOverrideCount[];
  selectedEntity: SelectedEntity;
  onSelectEntity: (entity: SelectedEntity) => void;
  orgName: string;
}

const ROW_BASE = 'cursor-pointer border-b border-border-tertiary px-3 py-2.5';

const getRowClasses = (isSelected: boolean) =>
  cn(
    ROW_BASE,
    isSelected
      ? 'bg-bg-accent-strong-primary text-white'
      : 'hover:bg-bg-tertiary',
  );

export const EntitySelectorPanel: React.FC<EntitySelectorPanelProps> = ({
  users,
  selectedEntity,
  onSelectEntity,
  orgName,
}) => {
  const isOrgSelected = selectedEntity.type === 'org';
  const [filterOverrides, setFilterOverrides] = useState(false);

  const filteredUsers = useMemo(
    () => (filterOverrides ? users.filter((u) => u.overrideCount > 0) : users),
    [users, filterOverrides],
  );

  return (
    <Stack align="stretch">
      <PanelHeader
        title="Select Scope"
        cornerRadius="tl"
        sticky
        action={
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={filterOverrides ? 'default' : 'outline'}
                onClick={() => setFilterOverrides((v) => !v)}
                aria-label={
                  filterOverrides
                    ? 'Show all users'
                    : 'Show only users with overrides'
                }
              >
                <Icon name="Filter" size="auto" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {filterOverrides
                ? 'Show all users'
                : 'Show only users with overrides'}
            </TooltipContent>
          </Tooltip>
        }
      />
      <div
        className={getRowClasses(isOrgSelected)}
        onClick={() => onSelectEntity({ type: 'org' })}
      >
        <Stack gap={2} align="start" direction="horizontal">
          <Icon
            name="Building2"
            className={cn(
              'mt-0.5 shrink-0',
              isOrgSelected ? 'text-white' : 'text-text-secondary',
            )}
          />
          <Stack align="start">
            <Text
              inline
              size="xs"
              weight={isOrgSelected ? 'semibold' : 'medium'}
              className={isOrgSelected ? 'text-white' : undefined}
            >
              Organization Level
            </Text>
            <Text
              inline
              size="xs"
              color={isOrgSelected ? undefined : 'tertiary'}
              className={isOrgSelected ? 'text-white/80' : undefined}
            >
              {orgName}
            </Text>
          </Stack>
        </Stack>
      </div>

      <div className="border-b border-border-tertiary px-3 py-2">
        <Text size="xs" color="tertiary">
          Customize per User
        </Text>
      </div>

      <Stack align="stretch" className="overflow-y-auto">
        {filteredUsers.map((user) => {
          const isSelected =
            selectedEntity.type === 'user' &&
            selectedEntity.userId === user.userId;

          return (
            <div
              key={user.userId}
              className={cn(getRowClasses(isSelected), 'last:border-b-0')}
              onClick={() =>
                onSelectEntity({
                  type: 'user',
                  userId: user.userId,
                  userName: user.name,
                })
              }
            >
              <Stack
                gap={2}
                justify="between"
                align="center"
                direction="horizontal"
              >
                <Stack
                  gap={2}
                  align="start"
                  className="min-w-0 flex-1"
                  direction="horizontal"
                >
                  <Icon
                    name="User"
                    className={cn(
                      'mt-0.5 shrink-0',
                      isSelected ? 'text-white' : 'text-text-secondary',
                    )}
                  />
                  <Stack align="start" className="min-w-0">
                    <Text
                      inline
                      size="xs"
                      weight={isSelected ? 'semibold' : 'regular'}
                      className={cn('line-clamp-2', isSelected && 'text-white')}
                    >
                      {user.name.includes('@') ? (
                        <>
                          {user.name.split('@')[0]}
                          <wbr />@{user.name.split('@')[1]}
                        </>
                      ) : (
                        user.name
                      )}
                    </Text>
                    {user.name !== user.email && (
                      <Text
                        size="xs"
                        color={isSelected ? undefined : 'tertiary'}
                        className={isSelected ? 'text-white/80' : undefined}
                      >
                        {user.email.includes('@') ? (
                          <>
                            {user.email.split('@')[0]}
                            <wbr />@{user.email.split('@')[1]}
                          </>
                        ) : (
                          user.email
                        )}
                      </Text>
                    )}
                  </Stack>
                </Stack>
                {user.overrideCount > 0 && (
                  <Badge variant="info" className="shrink-0">
                    {user.overrideCount}
                  </Badge>
                )}
              </Stack>
            </div>
          );
        })}
        {filteredUsers.length === 0 && (
          <EmptyState
            message={
              filterOverrides
                ? 'No users with overrides'
                : 'No users in organization'
            }
          />
        )}
      </Stack>
    </Stack>
  );
};
